// @flow
import * as React from "react";
import values from "lodash/values";
import sum from "lodash/sum";
import cx from "classnames";
import { Link, withRouter } from "react-router-dom";
import SVG from "react-inlinesvg";

import Dropdown from "../../../Dropdown";

import styles from "./NavTabs.scss";
import dropdownStyles from "./dropdownStyles.scss";

import LongLeftArrow from "../../../../assets/icons/other/longRightArrow.svg";
import ThreeDotsIcon from "../../../../assets/icons/other/threeDots.svg";

type Tabs = [
  {
    label: string,
    path: string
  }
];

type NavTabsProps = {
  tabs: Tabs,
  history: {
    location: { pathname: string },
    push: (path: string) => void,
    goBack: () => void
  },
  backPath: string
};

type NavTabsState = {
  numberOfLinksToShow: number
};

class NavTabs extends React.Component<NavTabsProps, NavTabsState> {
  static defaultProps = {
    tabs: [],
    backPath: "/"
  };

  navRef = React.createRef();

  tabsContainerRef = React.createRef();

  backLinkRef = React.createRef();

  dropdownRef = React.createRef();

  constructor(props) {
    super(props);

    const { tabs } = this.props;
    this.state = {
      numberOfLinksToShow: tabs.length
    };
  }

  componentDidMount() {
    const { tabs } = this.props;

    this.setNumberOfLinksToShow(tabs.length);
    window.addEventListener("resize", this.handleWindowResize);
    window.addEventListener("load", this.handleWindowResize);
    this.handleWindowResize();
  }

  componentDidUpdate(prevProps) {
    const { tabs } = this.props;
    if (prevProps.tabs.length !== tabs.length) {
      this.setNumberOfLinksToShow(tabs.length);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowResize);
    window.removeEventListener("load", this.handleWindowResize);
  }

  setNumberOfLinksToShow = (numberOfLinksToShow: number) => this.setState({ numberOfLinksToShow });

  getWidthOfElement = (
    node: ?HTMLElement,
    withMargin: boolean = false,
    withoutPadding: boolean = false
  ) => {
    if (!node) {
      return 0;
    }
    const { marginLeft, marginRight, paddingLeft, paddingRight } = window.getComputedStyle(node);

    let result = node.offsetWidth;

    if (withMargin) {
      result += parseFloat(marginLeft);
      result += parseFloat(marginRight);
    }

    if (withoutPadding) {
      result -= parseFloat(paddingLeft);
      result -= parseFloat(paddingRight);
    }

    return result;
  };

  handleWindowResize = () => {
    this.calculateLinksToShow();
  };

  calculateLinksToShow = () => {
    const numberOfLinksToShow = this.getNumberOfLinksToShow();

    this.setState({ numberOfLinksToShow });
  };

  getNumberOfLinksToShow = () => {
    const tabsNodes =
      (this.tabsContainerRef.current && this.tabsContainerRef.current.childNodes) || {};
    const backLinkNode = this.backLinkRef.current;
    const navNode = this.navRef.current;
    const dropdownNode = this.dropdownRef.current;

    const backLinkWidth = this.getWidthOfElement(backLinkNode, true);
    const navWidth = this.getWidthOfElement(navNode, false, true);
    const dropdownWidth = this.getWidthOfElement(dropdownNode, true);
    const tabsWidths = values(tabsNodes).map(node => this.getWidthOfElement(node, true));
    const numberOfLinksToShow = tabsWidths.reduce((fittingLinksCount, width, index) => {
      const willFit =
        navWidth - backLinkWidth - dropdownWidth - sum(tabsWidths.slice(0, index + 1)) > 0;

      return willFit ? fittingLinksCount + 1 : fittingLinksCount;
    }, 0);

    return numberOfLinksToShow;
  };

  getActiveIndex = () => {
    const {
      history: {
        location: { pathname }
      },
      tabs
    } = this.props;

    return tabs.findIndex(route => pathname === route.path);
  };

  goBack = e => {
    const {
      history: { push },
      backPath
    } = this.props;

    e.preventDefault();

    push(backPath);
  };

  getAdditionalTabs = () => {
    const {
      tabs,
      history: { push }
    } = this.props;
    const { numberOfLinksToShow } = this.state;

    return tabs.slice(numberOfLinksToShow).map(({ label, path }) => ({
      text: () => label,
      title: label,
      onClick: closePopover => {
        closePopover();
        push(path);
      }
    }));
  };

  render() {
    const { tabs } = this.props;
    const { numberOfLinksToShow } = this.state;
    const activeIndex = this.getActiveIndex();

    return (
      <nav className={styles.navTabsContainer}>
        <nav className={styles.nav} ref={this.navRef}>
          <div className={styles.tabsContainer}>
            <div className={styles.tabs} ref={this.tabsContainerRef}>
              {tabs.map(({ label, path }, tabIndex) => (
                <Link
                  className={cx(styles.tab, {
                    [styles.activeTab]: tabIndex === activeIndex,
                    [styles.hiddenTab]: tabIndex >= numberOfLinksToShow,
                    [styles.lastTab]: tabIndex + 1 === numberOfLinksToShow
                  })}
                  to={path}
                  // eslint-disable-next-line react/no-array-index-key
                  key={tabIndex}
                  data-testid="headerTab"
                >
                  {label}
                </Link>
              ))}
            </div>
            {numberOfLinksToShow < tabs.length && (
              <div className={styles.dropdownContainer} ref={this.dropdownRef}>
                <Dropdown
                  icon={ThreeDotsIcon}
                  options={this.getAdditionalTabs()}
                  activeIndex={activeIndex - numberOfLinksToShow}
                  additionalStyles={dropdownStyles}
                  popperWidth={150}
                />
              </div>
            )}
          </div>
          <div className={styles.backLinkContainer}>
            {/* Done it to keep styles consistent. This element also acts like a hyperlink. */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className={styles.backLink} onClick={this.goBack} ref={this.backLinkRef}>
              <SVG cacheGetRequests className={styles.backIcon} src={LongLeftArrow} />
              Zurück
            </a>
          </div>
        </nav>
      </nav>
    );
  }
}

export default withRouter(NavTabs);
